/**
 * Which surfaces hide the site footer.
 *
 * Two kinds of page hide it, for two different reasons:
 *
 *  · an APP surface — Discover, the chat, the trips planner, the saved list —
 *    fills the viewport and owns its own scrolling. A footer under it is a
 *    second scroll region below something that never ends, and scrolling past
 *    a live map to reach "Top categories" reads as a mistake;
 *  · a FUNNEL step — packages, details, checkout, thank-you, bucket, a stay
 *    confirmation — where anything that leads away is a leak.
 *
 * Landing pages keep the footer even when they carry the same tool:
 * `/ai-travel-planner` and `/trip-planner` are marketing pages whose whole job
 * is the internal link graph, and the footer is a large part of it.
 *
 * Extracted from `_layout.tsx` so the list is one testable thing. It was a
 * chain of `endsWith` inline in the component, and moving the chat from
 * `/concierge` to `/ai-chat` silently left it behind — the footer came back
 * under a full-height chat and map.
 */
const APP_SURFACES = [
  "/discover",
  "/ai-chat",
  "/trips",
  "/liked",
] as const;

const FUNNEL_STEPS = [
  "/packages",
  "/details",
  "/checkout",
  "/thank-you",
  "/bucket",
  "/bucket/checkout",
] as const;

const FUNNEL_FRAGMENTS = ["/stays/confirmation/", "/stays/receipt/"] as const;

export function isFooterHiddenRoute(pathname: string): boolean {
  if (APP_SURFACES.some((p) => pathname.endsWith(p))) return true;
  if (FUNNEL_STEPS.some((p) => pathname.endsWith(p))) return true;
  return FUNNEL_FRAGMENTS.some((p) => pathname.includes(p));
}

/**
 * Pages that keep the footer on a phone or tablet (owner, 2026-09-26).
 *
 * Below `lg` the bottom tab bar already does the navigating, so the long
 * footer only earns its space on Home and on the pages people come to FOR
 * what the footer holds: about, contact, FAQ, the legal pages and the blog.
 * Everywhere else it is hidden with CSS only, so crawlers still read its
 * links on every page; computers keep it everywhere.
 */
const PHONE_FOOTER_PAGES = [
  "about",
  "contact",
  "faq",
  "become-a-partner",
  "privacy-policy",
  "cookie-policy",
  "cancellation-policy",
  "terms-and-conditions",
  "terms-of-service",
  "blog",
] as const;

export function isFooterShownOnPhone(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  // `/en` is Home.
  if (parts.length <= 1) return true;
  return (PHONE_FOOTER_PAGES as readonly string[]).includes(parts[1]!);
}
